import React, { useEffect } from 'react'
import { PromotionDataContext, useInitPromotionData } from '@obeta/data/lib/hooks/usePromotionData'
import { usePromotionItemsFromActiveCart } from './hooks/usePromotionItemsFromActiveCart'
import { CustomEditor, EPlugins } from '../reactPageEditor/CustomEditor'
import { calculateProgress } from '@obeta/utils/lib/promotion'
import { useHistory } from '@obeta/data/lib/hooks/useHistoryApi'
import { Box, Stack, Typography } from '@mui/material'
import { PromotionDateRange } from './promotionDateRange'
import { useTranslation } from 'react-i18next'

export interface IPromotionHeaderProps {
  setData: (data) => void
  promotionId: string
  isFinished?: boolean
}

export const PromotionHeader: React.FC<IPromotionHeaderProps> = (props) => {
  const { setData, promotionId, isFinished = false } = props
  const { replace } = useHistory()
  const { t } = useTranslation()
  const promotionData = useInitPromotionData(promotionId)
  const { content, criteriaType, setProgress, loading, name, startTime, endTime } = promotionData
  const promotionItems = usePromotionItemsFromActiveCart()
  const promotionProgress = calculateProgress(promotionItems, criteriaType)
  const [firstStrapiImage, setFirstStrapiImage] = React.useState(null)

  useEffect(() => {
    setProgress(promotionProgress)
  }, [promotionProgress, setProgress])

  // Find the first image in the slate content identified by the pluginid: "de/obeta/plugins/strapi-image"
  const findFirstImage = React.useCallback(() => {
    if (!content) return null

    // Helper function to recursively search for the image node in the nested structure
    const findImageNode = (item) => {
      // Check if current item is an image plugin
      if (item && item.plugin && item.plugin.id === 'de/obeta/plugins/strapi-image') {
        return item
      }

      // Check rows if they exist
      if (item.rows && Array.isArray(item.rows)) {
        for (const row of item.rows) {
          const found = findImageNode(row)
          if (found) return found
        }
      }

      // Check cells if they exist
      if (item.cells && Array.isArray(item.cells)) {
        for (const cell of item.cells) {
          const found = findImageNode(cell)
          if (found) return found
        }
      }

      return null
    }

    return findImageNode(content)
  }, [content])

  // Find and store the first image when content changes
  useEffect(() => {
    const image = content ? findFirstImage() : null
    setFirstStrapiImage(image?.dataI18n?.default?.url)
  }, [content, findFirstImage])

  useEffect(() => {
    // Make the image data available to parent components via the setData callback
    setData({
      ...promotionData,
      firstStrapiImage,
    })
    //just do a string name comparison instead of deepeqal object
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setData, promotionData.name, firstStrapiImage])

  useEffect(() => {
    if (!content && !loading) {
      replace('/')
    }
  }, [content, loading, replace])

  if (!content) {
    return null
  }

  if (isFinished) {
    return (
      <Stack
        direction={{ xs: 'column', md: 'row' }}
        alignItems={'flex-start'}
        justifyContent={'flex-start'}
        gap={'1.5rem'}
      >
        <Box
          component="img"
          src={firstStrapiImage || ''}
          alt="Aktionsbild"
          sx={{
            maxWidth: { xs: '100%', md: '150px' },
            borderRadius: '4px',
            border: '1px solid #ccc',
          }}
        />
        <Stack direction={'column'} gap="1rem">
          <Typography variant="headline2Bold">
            {t('PROMOTIONS.PROMOTION_EXPIRED_PAGE_TITLE')}
          </Typography>
          <Stack direction={'row'} gap="1rem" alignItems={'center'}>
            <Typography color="text.secondary" variant="bodyBold">
              {name}
            </Typography>
            <PromotionDateRange startTime={startTime} endTime={endTime} />
          </Stack>
          <Typography variant="body1">{t('PROMOTIONS.PROMOTION_EXPIRED_PAGE_DETAIL')}</Typography>
        </Stack>
      </Stack>
    )
  }

  return (
    <PromotionDataContext.Provider value={promotionData}>
      <CustomEditor
        value={content}
        pluginsList={[
          EPlugins.Image,
          EPlugins.ProgressBar,
          EPlugins.PromotionLayout,
          EPlugins.RichText,
        ]}
      />
    </PromotionDataContext.Provider>
  )
}
